<script setup lang="ts">
import type { DailyCheckinRewardView } from '~/types/daily-checkin';
import { getAssetsUrlForCDN } from '@/utils/template';

const props = defineProps<{
  reward: DailyCheckinRewardView;
  // Esconder O QUE é o prêmio deste degrau (arte e rótulo), mantendo a célula e
  // o "Nº Dia". A trilha continua do mesmo tamanho: o jogador segue vendo
  // quantos dias faltam, só não vê o que vem — que é a diferença entre criar
  // expectativa e encolher a promoção na cara dele.
  hidePrize?: boolean;
}>();

// Toda célula abre o popup do prêmio daquele degrau.
const emit = defineEmits<{ (e: 'select', position: number): void }>();

// Trilha sequencial: o selo é a ORDEM na fila, não uma data — o degrau 7 pode
// cair no dia 12 do mês. "Nº Dia": o rótulo é ordinal, não uma data.
const badge = computed(() => `${props.reward.position}º Dia`);

// Rótulo do card: o `cardLabel` do cadastro ("100X"), curto de propósito — o
// `title` ("Baú de Coins") é a manchete do popup. São dois campos justamente
// porque a célula tem 65px e não comporta o nome inteiro.
//
// Sem `cardLabel` cadastrado, cai no primeiro token do título, que era o
// recurso usado antes de o contrato trazer os dois textos.
const cardLabel = computed(
  () => props.reward.cardLabel || props.reward.title.split(' ')[0] || '',
);

const footLabel = computed(() =>
  props.reward.state === 'claimed' ? 'RESGATADO' : cardLabel.value,
);

const prizeImage = computed(() => getAssetsUrlForCDN(props.reward.imageUrl));

const asset = (file: string) =>
  getAssetsUrlForCDN(`/assets/images/daily-checkin/${file}`);
</script>

<template>
  <button
    class="dc-cell"
    :class="[
      `dc-cell--${reward.state}`,
      {
        'dc-cell--wide': reward.isFeatured,
        'dc-cell--today': reward.isToday,
      },
    ]"
    type="button"
    @click="emit('select', reward.position)"
    @dragstart.prevent
  >
    <!-- Selo do degrau no canto superior esquerdo. Nos estados de faixa cheia
         ele não consome altura: a margem negativa deixa a faixa cobrir a
         célula toda por baixo. -->
    <span class="dc-cell__badge">{{ badge }}</span>

    <!-- Escondido: um ponto de interrogação no lugar da arte. Fica no MESMO
         espaço da imagem, então a célula não muda de tamanho quando o operador
         liga a opção.

         O `?` é DECORATIVO e o recado vai num texto só de leitor de tela. Aqui
         existiu um `aria-label` no próprio <span>: elemento genérico não aceita
         nome acessível, então o rótulo era ignorado e o nome do botão ficava
         sendo o conteúdo — "3º Dia ?". -->
    <template v-if="hidePrize">
      <span
        aria-hidden="true"
        class="dc-cell__img dc-cell__img--hidden"
        >?</span
      >
      <span class="sr-only">Prêmio ainda não revelado</span>
    </template>
    <img
      v-else
      alt=""
      class="dc-cell__img"
      loading="lazy"
      :src="prizeImage"
    />

    <span class="dc-cell__foot">
      <!-- Só o degrau resgatável vira botão. A legenda é a MESMA do popup que a
           célula abre, do tema do prêmio: escrita aqui, o operador trocava o
           botão pra "PEGAR" e a trilha continuava dizendo RESGATAR — duas
           palavras pro mesmo gesto, uma do lado da outra. -->
      <span
        v-if="reward.state === 'claimable'"
        class="dc-cell__claim"
        >{{ reward.theme.buttonLabel }}</span
      >

      <template v-else>
        <span class="dc-cell__label">{{ hidePrize ? '' : footLabel }}</span>

        <img
          v-if="reward.state === 'claimed'"
          alt=""
          class="dc-cell__mark dc-cell__mark--check"
          :src="asset('check.svg')"
        />
      </template>
    </span>
  </button>
</template>

<style scoped lang="scss">
// Toda a geometria do Figma é expressa em múltiplos de `--dc-u`, a unidade que
// vale 1px quando a célula tem os 65px do design. Assim a célula inteira
// (selo, imagem, faixa, tipografia) encolhe junto em telas estreitas, sem
// precisar de breakpoint — o modal define `--dc-cell`.
.dc-cell {
  --dc-u: calc(var(--dc-cell) / 65);
  // Halo do estado (só o resgatável tem). Vive numa var porque o anel de
  // "hoje" anima `box-shadow` e uma animação sobrescreve a propriedade
  // inteira — sem a var, o degrau resgatável perderia o brilho laranja ao
  // virar o degrau de hoje, que é justamente o caso mais comum.
  --dc-glow: 0 0 0 0 rgba(0, 0, 0, 0);

  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  // A largura vem da trilha do grid (1fr); `--dc-cell` é o valor nominal da
  // trilha e serve de base pra `--dc-u` e pra altura quadrada.
  width: 100%;
  height: var(--dc-cell);
  padding: 0;
  border: 1px solid;
  border-radius: calc(8 * var(--dc-u));
  // `clip` (não `hidden`) pelo mesmo motivo da roletinha: `hidden` cria um
  // scroll container e o ajuste de foco do clique desloca o conteúdo.
  overflow: clip;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:active {
    filter: brightness(0.94);
  }
}

// Degrau em destaque: ocupa duas trilhas + o gap entre elas.
.dc-cell--wide {
  grid-column: span 2;
}

/* ---------- selo do degrau ---------- */
.dc-cell__badge {
  position: relative;
  z-index: 2;
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  // Largura mínima do Figma; com o rótulo "Dia" o selo pode passar dela em
  // números de dois dígitos, então ela não pode ser fixa.
  min-width: calc(45.5 * var(--dc-u));
  height: calc(14.625 * var(--dc-u));
  padding-inline: calc(8.125 * var(--dc-u));
  border: 1px solid;
  // Raio grande só no canto inferior direito, como no Figma.
  border-bottom-right-radius: calc(81.25 * var(--dc-u));
  color: #fff;
  font-size: calc(10 * var(--dc-u));
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

/* ---------- arte do prêmio ---------- */
// Caixa fixa de 65x51 centrada (mesmo nas células duplas, onde a arte não
// estica). `contain` porque a imagem vem do core: cada prêmio tem proporção
// própria e não pode ser cortado.
.dc-cell__img {
  position: absolute;
  z-index: 1;
  left: 50%;
  top: calc(50% + 7.63 * var(--dc-u));
  width: calc(65 * var(--dc-u));
  height: calc(51 * var(--dc-u));
  transform: translate(-50%, -50%);
  object-fit: contain;
  pointer-events: none;
  -webkit-user-drag: none;
}

// Ocupa a mesma caixa da arte, então ligar a opção não mexe na geometria da
// célula. Branco a 40%: presente o bastante pra dizer "tem prêmio aqui", apagado
// o bastante pra não competir com o degrau resgatável ao lado.
.dc-cell__img--hidden {
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: calc(28 * var(--dc-u));
  font-weight: 800;
  line-height: 1;
}

/* ---------- faixa inferior ---------- */
.dc-cell__foot {
  position: relative;
  z-index: 2;
  display: flex;
  flex-shrink: 0;
  width: 100%;
  height: calc(37 * var(--dc-u));
  align-items: flex-end;
  justify-content: center;
  padding-inline: calc(4 * var(--dc-u));
  padding-block: calc(1 * var(--dc-u));
}

.dc-cell__label {
  flex: 1 0 0;
  // Deixa o rótulo encolher junto com a célula em vez de esticar a trilha do
  // grid (o texto é clipado pelo `overflow: clip` da célula).
  min-width: 0;
  color: #fff;
  font-size: calc(12 * var(--dc-u));
  font-weight: 800;
  line-height: 1.2;
  text-align: right;
}

.dc-cell__claim {
  display: flex;
  flex: 1 0 0;
  align-items: center;
  justify-content: center;
  padding: calc(2 * var(--dc-u)) calc(2 * var(--dc-u)) calc(3 * var(--dc-u));
  border-radius: calc(5 * var(--dc-u));
  background: #ff3700;
  color: #fff;
  font-size: calc(9 * var(--dc-u));
  font-weight: 800;
  line-height: 1.15;
}

.dc-cell__mark {
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  -webkit-user-drag: none;
}

// Proporção do próprio asset (35x30 no Figma), não uma caixa genérica.
.dc-cell__mark--check {
  top: calc(50% + 1.5 * var(--dc-u));
  width: calc(35 * var(--dc-u));
  height: calc(30 * var(--dc-u));
}

/* ---------- estados ---------- */
// São três, e a língua de cor é curta: azul = ainda vem (inclusive o degrau que
// não dá mais tempo de alcançar), laranja = dá pra pegar agora, verde = pego.

// Os gradientes do Figma vêm com ângulos de 180.13°–180.44° (arredondamento do
// editor); 180deg é visualmente idêntico. As faixas usam um radial elíptico
// ancorado na base, com raios convertidos do gradientTransform do Figma.

// Azul do que ainda vem. Era navy (#203570) e passou a ser o mesmo azul que o
// popup usava no tom resgatável — a trilha e o popup falam a mesma língua.
.dc-cell--pending {
  border-color: #1b54cf;
  background: linear-gradient(180deg, #060642 32.836%, #0f0fa8 99.762%);

  .dc-cell__badge {
    border-color: #1b54cf;
    background: #1414dd;
  }

  .dc-cell__foot {
    background: radial-gradient(
      90.6% 63.5% at 50.8% 93.2%,
      #1414dd 0%,
      rgba(20, 20, 221, 0) 100%
    );
  }
}

// Único estado de faixa cheia: ela cobre a célula inteira e empilha o rótulo
// embaixo do check. O selo recua com margem negativa pra não somar altura.
.dc-cell--claimed {
  border-color: #17c964;
  background: linear-gradient(180deg, #004308 22.624%, #009d12 99.618%);

  .dc-cell__badge {
    margin-bottom: calc(-14.625 * var(--dc-u));
    border-color: #17c964;
    background: #009d12;
  }

  .dc-cell__foot {
    height: calc(65 * var(--dc-u));
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    background: radial-gradient(
      215% 82.4% at 50.8% 94.6%,
      #009d12 0%,
      rgba(0, 157, 18, 0) 100%
    );
  }

  .dc-cell__label {
    flex: 0 0 auto;
    width: 100%;
    height: calc(15 * var(--dc-u));
    font-size: calc(9 * var(--dc-u));
    text-align: center;
  }
}

// O único degrau em laranja é o que dá pra resgatar agora.
.dc-cell--claimable {
  --dc-glow: 0 0 calc(20 * var(--dc-u)) 0 #ff3700;

  border-color: #ff8766;
  background: linear-gradient(180deg, #330b00 32.836%, #ff3700 99.762%);
  box-shadow: var(--dc-glow);

  .dc-cell__badge {
    border-color: #ff8766;
    background: #ff3700;
  }

  .dc-cell__foot {
    padding-block: calc(3 * var(--dc-u));
    background: radial-gradient(
      90.6% 63.5% at 50.8% 93.2%,
      #ff3700 0%,
      rgba(255, 55, 0, 0) 100%
    );
  }

  // O brilho varre só este degrau — é o que o sinaliza como resgatável.

  // A faixa é um gradiente dentro de uma camada do TAMANHO da célula,
  // deslocada por translateX de -100% a 100%: assim ela cruza a célula inteira
  // e vale tanto pra célula simples quanto pra dupla, sem depender da largura.
  &::after {
    content: '';

    position: absolute;
    z-index: 3;
    top: -25%;
    left: 0;
    width: 100%;
    height: 150%;
    background: linear-gradient(
      105deg,
      rgba(255, 255, 255, 0) 42%,
      rgba(255, 255, 255, 0.6) 50%,
      rgba(255, 255, 255, 0) 58%
    );
    pointer-events: none;
    animation: dc-shine 3.2s ease-in-out infinite;
  }
}

/* ---------- "você está aqui" ---------- */
// Anel dourado oscilante no degrau de hoje, desenhado POR FORA. É `box-shadow`
// da própria célula (e não uma camada filha) por causa do `overflow: clip`:
// filho que passa da borda é cortado, mas a sombra do próprio elemento não.

// O halo do estado entra junto, via `--dc-glow`, senão a animação apagaria o
// brilho laranja do degrau resgatável.
.dc-cell--today {
  animation: dc-today 4.2s ease-in-out infinite;
}

// Respiração, não pisca-pisca: o ciclo é longo e ASSIMÉTRICO — sobe, recua um
// pouco, volta a subir de leve e desce. Um seno de 0→100% bate certinho como
// metrônomo e é o que dava o ar mecânico.
// São duas sombras: o traço fino (spread, sem blur) desenha o anel, e o halo
// borrado atrás é o que dá o brilho. Separar as duas deixa subir o brilho sem
// engordar o anel. Todos os passos têm as mesmas 3 sombras, senão o navegador
// não interpola entre eles.
@keyframes dc-today {
  0%,
  20% {
    box-shadow:
      var(--dc-glow),
      0 0 0 calc(0.75 * var(--dc-u)) rgba(255, 209, 90, 0.5),
      0 0 calc(3 * var(--dc-u)) rgba(255, 197, 49, 0.3);
  }

  46% {
    box-shadow:
      var(--dc-glow),
      0 0 0 calc(1 * var(--dc-u)) #ffd964,
      0 0 calc(11 * var(--dc-u)) calc(1 * var(--dc-u)) rgba(255, 197, 49, 0.9);
  }

  64% {
    box-shadow:
      var(--dc-glow),
      0 0 0 calc(0.9 * var(--dc-u)) rgba(255, 209, 90, 0.72),
      0 0 calc(5 * var(--dc-u)) rgba(255, 197, 49, 0.5);
  }

  80% {
    box-shadow:
      var(--dc-glow),
      0 0 0 calc(1 * var(--dc-u)) rgba(255, 217, 100, 0.92),
      0 0 calc(8 * var(--dc-u)) calc(0.5 * var(--dc-u)) rgba(255, 197, 49, 0.72);
  }

  100% {
    box-shadow:
      var(--dc-glow),
      0 0 0 calc(0.75 * var(--dc-u)) rgba(255, 209, 90, 0.5),
      0 0 calc(3 * var(--dc-u)) rgba(255, 197, 49, 0.3);
  }
}

// @keyframes (e não transition) por causa do reset global de transition em
// main.scss, que zera transições no app inteiro.
@keyframes dc-shine {
  0%,
  55% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}
</style>
